import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Eye, 
  Camera, 
  Drone, 
  HardDrive,
  ArrowRight,
  Code,
  Flame,
  Package,
  Cpu,
  ShieldCheck
} from 'lucide-react';

const ServicesSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const serviceCategories = [
    {
      title: "Cyber Security & Intelligence",
      description: "Advanced cybersecurity solutions and intelligence gathering for critical infrastructure protection",
      icon: Shield,
      image: "https://images.pexels.com/photos/5475750/pexels-photo-5475750.jpeg",
      services: ["Cyber Security", "Cyber Intelligence", "Forensics VAPT", "CDR IPDR Analysis"],
      link: "/services/cyber-security"
    },
    {
      title: "Counter-Intelligence & Surveillance",
      description: "Specialized equipment and services for counter-intelligence operations and surveillance detection",
      icon: Eye,
      image: "https://images.pexels.com/photos/8728559/pexels-photo-8728559.jpeg",
      services: ["Spy Devices", "Special Equipment", "Room Debugging", "Antispy Devices"],
      link: "/services/counter-intelligence"
    },
    {
      title: "Surveillance & Monitoring Systems",
      description: "Comprehensive surveillance solutions with advanced monitoring and networking capabilities",
      icon: Camera,
      image: "https://images.unsplash.com/photo-1589935447067-5531094415d1",
      services: ["CCTV Surveillance", "Video Door Phones", "Networking Solutions"],
      link: "/services/surveillance"
    },
    {
      title: "Drone Operations & Services",
      description: "Professional drone services with certified pilots for reconnaissance and surveillance missions",
      icon: Drone,
      image: "https://images.pexels.com/photos/7663144/pexels-photo-7663144.jpeg",
      services: ["Drone Services", "Certified Drone Pilots", "Aerial Surveillance"],
      link: "/services/drone-operations"
    },
    {
      title: "Digital Forensics & Recovery",
      description: "Expert digital forensics, data recovery solutions, and custom software development",
      icon: HardDrive,
      image: "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b",
      services: ["Data Recovery", "Software Development", "Digital Investigation", "Emergency Equipment"],
      link: "/services/digital-forensics"
    },
    {
      title: "Software Development",
      description: "Customised, critical, and advanced software development with AI and cloud-native architectures.",
      icon: Code,
      image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c",
      services: ["Critical Software", "AI Applications", "Cloud & DevOps"],
      link: "/services/software-development"
    },
    {
      title: "Fire Fighting System",
      description: "Comprehensive fire detection, suppression, and emergency response solutions.",
      icon: Flame,
      image: "https://images.unsplash.com/photo-1509228468518-180dd4864904",
      services: ["Detection Systems", "Suppression Systems", "Alarms & Extinguishers"],
      link: "/services/fire-fighting-system"
    },
    {
      title: "Warehouse Management",
      description: "Advanced RFID-based inventory tracking, automated forklift operations, and intelligent warehouse management solutions for optimized logistics and supply chain operations.",
      icon: Package,
      image: "https://images.unsplash.com/photo-1689942010216-dc412bb1e7a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBhdXRvbWF0aW9ufGVufDB8fHx8MTc1MjI0ODI0MXww&ixlib=rb-4.1.0&q=85",
      services: ["RFID Tracking", "Automated Forklifts", "Inventory Management", "Warehouse Automation"],
      link: "/services/warehouse-management"
    },
    {
      title: "Research & Development",
      description: "Custom embedded systems, IoT solutions, and specialized spy device development with cutting-edge electronics and software integration for intelligence and security applications.",
      icon: Cpu,
      image: "https://images.unsplash.com/photo-1650530415027-dc9199f473ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxlbGVjdHJvbmljcyUyMGRldmVsb3BtZW50fGVufDB8fHx8MTc1MjI0ODI0Nnww&ixlib=rb-4.1.0&q=85",
      services: ["Embedded Systems", "IoT Development", "Spy Devices", "Custom Electronics"],
      link: "/services/research-development"
    },
    {
      title: "Safety Equipment",
      description: "Comprehensive physical security solutions including access control systems, perimeter security, and advanced barrier technologies for critical infrastructure protection.",
      icon: ShieldCheck,
      image: "https://images.unsplash.com/photo-1569784210445-990e7e44ccfe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGJhcnJpZXJzfGVufDB8fHx8MTc1MjI0ODI5MXww&ixlib=rb-4.1.0&q=85",
      services: ["Boom Barriers", "Access Control", "Turnstiles", "Perimeter Security"],
      link: "/services/safety-equipment"
    }
  ];

  return (
    <section className="section-padding bg-gray-dark" ref={ref}>
      <div className="container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="heading-font text-5xl md:text-6xl font-bold mb-6">
            <span className="text-royal-gold">Our</span> <span className="text-off-white">Services</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Comprehensive security solutions tailored for government agencies and enterprise clients. 
            From cybersecurity to digital forensics, we deliver cutting-edge technology and expertise.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 justify-center items-center">
          {serviceCategories.map((category, index) => (
            <Link
              key={index}
              to={category.link}
              className="block bg-gray-900 hover:bg-royal-gold/10 text-off-white hover:text-royal-gold text-center font-semibold text-lg py-8 rounded-16 shadow-md transition-all duration-300 border border-gray-800"
              style={{ minHeight: '80px' }}
            >
              {category.title}
            </Link>
          ))}
        </div>

        {/* Bottom CTA
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-16"
        >
          <div className="glass-morphism rounded-24 p-8 max-w-2xl mx-auto">
            <h3 className="heading-font text-3xl font-semibold text-off-white mb-4">
              Need a Custom Solution?
            </h3>
            <p className="text-gray-300 mb-6">
              We work closely with government agencies to develop tailored security solutions 
              that meet your specific operational requirements.
            </p>
            <Link to="/#contact" className="cta-button">
              Contact Our Experts
            </Link>
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};

export default ServicesSection;